/**
 * Peer Evaluation API Client
 *
 * API functions for 360-degree feedback peer evaluations.
 * Includes both authenticated endpoints and public endpoints.
 */

import { apiRequest, getApiBaseUrl } from './api';

// ============================================
// TYPES
// ============================================

/**
 * Task attached to a peer evaluation
 */
export interface PeerEvaluationTask {
  id: number;
  taskId: number;
  name: string;
  beschrijving: string | null;
  score: number | null;
  remarks: string | null;
}

/**
 * Competency attached to a peer evaluation
 */
export interface PeerEvaluationCompetency {
  id: number;
  competencyId: number;
  name: string;
  beschrijving: string | null;
  type: 'basis' | 'vak';
  score: number | null;
  remarks: string | null;
}

/**
 * Full peer evaluation with relations
 */
export interface PeerEvaluation {
  id: number;
  employeeId: number;
  employeeName: string;
  evaluatorId: number;
  evaluatorName: string;
  createdBy: number;
  createdByName: string;
  token: string;
  status: 'concept' | 'ingediend';
  submittedAt: string | null;
  generalRemarks: string | null;
  createdAt: string;
  updatedAt: string;
  tasks: PeerEvaluationTask[];
  competencies: PeerEvaluationCompetency[];
}

/**
 * Input for creating a peer evaluation
 */
export interface CreatePeerEvaluationInput {
  employeeId: number;
  evaluatorId: number;
  taskIds: number[];
  competencyIds: number[];
}

/**
 * List response from API
 */
export interface PeerEvaluationsListResponse {
  data: PeerEvaluation[];
  total: number;
}

// ============================================
// PUBLIC TYPES (for /evaluatie/:token page)
// ============================================

/**
 * Task in public form (minimal data)
 */
export interface PublicTask {
  taskId: number;
  name: string;
  beschrijving: string | null;
  scoreCriteria: ScoreCriterion[];
}

/**
 * Competency in public form (minimal data)
 */
export interface PublicCompetency {
  competencyId: number;
  name: string;
  beschrijving: string | null;
  type: 'basis' | 'vak';
  scoreCriteria: ScoreCriterion[];
}

/**
 * Score criteria for reference
 */
export interface ScoreCriterion {
  score: number;
  label: string;
  beschrijving: string | null;
}

/**
 * Public peer evaluation form data
 */
export interface PublicPeerEvaluationForm {
  employeeName: string;
  tasks: PublicTask[];
  competencies: PublicCompetency[];
  scoreCriteria: ScoreCriterion[];
}

/**
 * Input for submitting peer evaluation
 */
export interface SubmitPeerEvaluationInput {
  taskRemarks: Array<{ taskId: number; score?: number; remarks?: string }>;
  competencyRemarks: Array<{ competencyId: number; score?: number; remarks?: string }>;
  generalRemarks?: string;
}

/**
 * Input for updating a peer evaluation (authenticated)
 */
export interface UpdatePeerEvaluationInput {
  tasks: Array<{ taskId: number; score: number | null; remarks?: string }>;
  competencies: Array<{ competencyId: number; score: number | null; remarks?: string }>;
  generalRemarks?: string;
}

/**
 * Submit success response
 */
export interface SubmitPeerEvaluationResponse {
  message: string;
  code: string;
}

// ============================================
// AUTHENTICATED API FUNCTIONS
// ============================================

export const peerEvaluationApi = {
  /**
   * Create a new peer evaluation
   *
   * @param input - Employee ID, evaluator ID, task IDs, and competency IDs
   * @returns Created peer evaluation with relations
   */
  async create(input: CreatePeerEvaluationInput): Promise<PeerEvaluation> {
    return apiRequest<PeerEvaluation>('/peer-evaluations', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Get all peer evaluations for an employee
   *
   * @param employeeId - Employee to get peer evaluations for
   * @returns List of peer evaluations
   */
  async getByEmployeeId(employeeId: number): Promise<PeerEvaluationsListResponse> {
    return apiRequest<PeerEvaluationsListResponse>(
      `/peer-evaluations/employee/${employeeId}`
    );
  },

  /**
   * Get a single peer evaluation by ID
   *
   * @param id - Peer evaluation ID
   * @returns Peer evaluation with relations
   */
  async getById(id: number): Promise<PeerEvaluation> {
    return apiRequest<PeerEvaluation>(`/peer-evaluations/${id}`);
  },

  /**
   * Delete a peer evaluation (only concept status)
   *
   * @param id - Peer evaluation ID to delete
   */
  async delete(id: number): Promise<void> {
    await apiRequest(`/peer-evaluations/${id}`, { method: 'DELETE' });
  },

  /**
   * Update a peer evaluation (concept status only)
   *
   * @param id - Peer evaluation ID to update
   * @param data - Task/competency scores and remarks
   * @returns Updated peer evaluation
   */
  async update(id: number, data: UpdatePeerEvaluationInput): Promise<PeerEvaluation> {
    return apiRequest<PeerEvaluation>(`/peer-evaluations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// ============================================
// PUBLIC API FUNCTIONS (No Auth)
// ============================================

/**
 * Public API client for peer evaluations
 *
 * These endpoints do not require authentication.
 * Used for the /evaluatie/:token page.
 */
export const publicPeerEvaluationApi = {
  /**
   * Get peer evaluation form by token
   *
   * @param token - Secure token from the peer evaluation link
   * @returns Form data with employee name, tasks, competencies, and score criteria
   * @throws Error if token is invalid or evaluation already submitted
   */
  async getByToken(token: string): Promise<PublicPeerEvaluationForm> {
    const response = await fetch(`${getApiBaseUrl()}/peer-evaluations/public/${token}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Request failed with status ${response.status}`);
    }

    return response.json() as Promise<PublicPeerEvaluationForm>;
  },

  /**
   * Submit peer evaluation
   *
   * @param token - Secure token from the peer evaluation link
   * @param data - Task remarks, competency remarks, and optional general remarks
   * @returns Success message
   * @throws Error if token is invalid, already submitted, or validation fails
   */
  async submit(
    token: string,
    data: SubmitPeerEvaluationInput
  ): Promise<SubmitPeerEvaluationResponse> {
    const response = await fetch(`${getApiBaseUrl()}/peer-evaluations/public/${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Request failed with status ${response.status}`);
    }

    return response.json() as Promise<SubmitPeerEvaluationResponse>;
  },
};
